import schoolsData from '../data/schools.json';
import baseResourcesData from '../data/baseResources.json';
import { Spell } from '../types';

interface SchoolWithModifiers {
  id: string;
  school: string;
  description: string;
  modifiers: Record<string, number>;
}

const schools = schoolsData as unknown as SchoolWithModifiers[];

/**
 * Returns the casting number modifier for a spell given the warband's school.
 * Primary school (same as warband) = +0.
 * Otherwise the modifier is looked up from SchoolsModifiers.
 */
export function getCastingNumberModifier(warbandSchoolId: string, spellSchoolId: string): number {
  if (!spellSchoolId || !warbandSchoolId) return 0;
  const normalised = (s: string) => s.trim().toLowerCase();
  if (normalised(warbandSchoolId) === normalised(spellSchoolId)) return 0;
  const warbandSchool = schools.find((s) => normalised(s.id) === normalised(warbandSchoolId));
  if (!warbandSchool) return 0;
  const mod = warbandSchool.modifiers[normalised(spellSchoolId)];
  return mod ?? 0;
}

/**
 * Adjusts all spells' castingNumber based on the warband's school.
 * Also applies any level-up spell improvements (each improvement reduces castingNumber by 1).
 */
export function applySchoolAndLevelUps(
  spells: Spell[],
  warbandSchoolId: string,
  spellImprovements: Record<string, number> = {}
): Spell[] {
  return spells.map((spell) => {
    const schoolMod = getCastingNumberModifier(warbandSchoolId, spell.schoolId);
    const improvement = spellImprovements[spell.id] ?? 0;
    return {
      ...spell,
      castingNumber: spell.baseCastingNumber + schoolMod - improvement,
    };
  });
}

interface BaseResourceEntry {
  id: string;
  affectedSpellIds?: string[];
}
const baseResources = baseResourcesData as BaseResourceEntry[];

/**
 * Reduces the castingNumber of spells that are boosted by owned base resources.
 * Each matching owned resource reduces the castingNumber by 1.
 */
export function applyBaseResourceCastingBonuses(
  spells: Spell[],
  ownedBaseResourceIds: string[]
): Spell[] {
  if (!ownedBaseResourceIds.length) return spells;
  const bonusBySpellId = new Map<string, number>();
  for (const resourceId of ownedBaseResourceIds) {
    const resource = baseResources.find((r) => r.id === resourceId);
    if (resource?.affectedSpellIds) {
      for (const spellId of resource.affectedSpellIds) {
        bonusBySpellId.set(spellId, (bonusBySpellId.get(spellId) ?? 0) + 1);
      }
    }
  }
  if (!bonusBySpellId.size) return spells;
  return spells.map((spell) => {
    const bonus = bonusBySpellId.get(spell.id);
    if (!bonus) return spell;
    return { ...spell, castingNumber: spell.castingNumber - bonus };
  });
}

/**
 * Filters spells by school relationship category for a given warband school.
 * Returns [primarySpells, alignedSpellsArrays, neutralSpellsArrays].
 *
 * - primary (modifier = 0): spells of the warband's own school
 * - aligned (modifier = 2): spells of schools with modifier 2 — grouped by school
 * - neutral (modifier = 4): spells of schools with modifier 4 — grouped by school
 * - opposed (modifier = 6): spells of schools with modifier 6 — grouped by school
 */
export function categoriseSpellsBySchool(
  allSpells: Spell[],
  warbandSchoolId: string
): {
  primarySpells: Spell[];
  alignedSpells: Spell[][];
  neutralSpells: Spell[][];
} {
  const normalised = (s: string) => s.trim().toLowerCase();
  const warbandSchool = schools.find((s) => normalised(s.id) === normalised(warbandSchoolId));

  const primarySpells: Spell[] = [];
  const alignedBySchool = new Map<string, Spell[]>();
  const neutralBySchool = new Map<string, Spell[]>();

  for (const spell of allSpells) {
    const mod = getCastingNumberModifier(warbandSchoolId, spell.schoolId);
    if (mod === 0) {
      primarySpells.push(spell);
    } else if (mod === 2) {
      if (!alignedBySchool.has(spell.schoolId)) alignedBySchool.set(spell.schoolId, []);
      alignedBySchool.get(spell.schoolId)!.push(spell);
    } else if (mod === 4) {
      if (!neutralBySchool.has(spell.schoolId)) neutralBySchool.set(spell.schoolId, []);
      neutralBySchool.get(spell.schoolId)!.push(spell);
    }
    // mod === 6 (opposed) — not shown in current UI groupings
  }

  // Order aligned/neutral groups by their school name
  const sortedAligned = Array.from(alignedBySchool.values());
  const sortedNeutral = Array.from(neutralBySchool.values());

  // Suppress unused variable warning for warbandSchool
  void warbandSchool;

  return { primarySpells, alignedSpells: sortedAligned, neutralSpells: sortedNeutral };
}
